import { ChangeDetectionStrategy, Component, computed, effect, inject, input } from '@angular/core';
import { Router } from '@angular/router';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { SharedMaterialUIModule } from '@shared/modules/shared-material-ui.module';
import {
  SearchableLookupOption,
  SearchableLookupSelectComponent,
} from '@shared/components/searchable-lookup-select/searchable-lookup-select.component';
import { PermissionService } from '@core/services/permission.service';
import { Permission } from '@shared/enums/permission.enum';
import { AffiliateStore } from '@features/affiliate/affiliate.store';
import { InterventionStore } from '../../../intervention.store';
import { ProviderAssignmentResponse, ProviderReferralAffiliateResponse } from '../../../intervention.model';

@Component({
  selector: 'provider-referral-panel',
  imports: [ReactiveFormsModule, SearchableLookupSelectComponent, ...SharedMaterialUIModule],
  templateUrl: './provider-referral-panel.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class ProviderReferralPanelComponent {
  protected readonly interventionStore = inject(InterventionStore);
  protected readonly affiliateStore = inject(AffiliateStore);
  private readonly permissionService = inject(PermissionService);
  private readonly router = inject(Router);

  readonly interventionId = input.required<string>();

  // The intervention's current stage. A referral can only be created while it is in Assessment or
  // Referral - once it has moved on (including Closed) the panel is a read-only record of what happened.
  readonly interventionStatus = input<string | null>(null);

  protected readonly selectAffiliateControl = new FormControl<string>('', { nonNullable: true });
  protected readonly reassignAffiliateControl = new FormControl<string>('', { nonNullable: true });
  protected readonly declineReasonControl = new FormControl<string>('', { nonNullable: true });
  protected readonly withdrawReasonControl = new FormControl<string>('', { nonNullable: true });

  protected readonly eligibleOptions = computed<SearchableLookupOption[]>(() =>
    this.affiliateStore.eligibleAffiliates().map((item) => ({
      id: item.id,
      name: `${item.name} ${item.surname}`.trim(),
      code: null,
    })),
  );

  // The affiliates a case can be reassigned to: one that has already responded (accepted, declined or withdrawn) cannot be offered the
  // same referral again, so it is left out rather than offered and refused by the API.
  protected readonly reassignOptions = computed<SearchableLookupOption[]>(() => {
    const responded = new Set((this.currentReferral()?.assignments ?? []).map((assignment) => assignment.affiliateId));
    return this.eligibleOptions().filter((option) => !responded.has(option.id));
  });

  protected readonly referralModel = computed<'EXPRESS' | 'SELECT' | null>(() => {
    const intervention = this.interventionStore.selected();
    if (!intervention) {
      return null;
    }

    const type = this.interventionStore
      .interventionTypes()
      .find((item) => item.id === intervention.interventionTypeId);

    return type?.referralModel ?? null;
  });

  // Most recently created referral for this intervention - interventions.store orders newest first.
  protected readonly currentReferral = computed(() => this.interventionStore.referrals()[0] ?? null);

  // Affiliate-portal callers only ever get their own pool/assignment entries back from the API
  // (see ProviderReferralService.ToReferralResponse), so index 0 is always "mine" for them. A pool
  // membership really is unique per affiliate per referral (reactivated/deactivated, never
  // duplicated), so indexing here is safe.
  protected readonly myPoolEntry = computed<ProviderReferralAffiliateResponse | null>(
    () => this.currentReferral()?.affiliates[0] ?? null,
  );

  // Unlike pool membership, an affiliate can accumulate more than one assignment row over time
  // (decline, get reassigned, accept) - assignments[0] isn't reliably "my current standing", so
  // this looks for the specific Accepted row instead of indexing blindly.
  protected readonly myAcceptedAssignment = computed<ProviderAssignmentResponse | null>(
    () => this.currentReferral()?.assignments.find((a) => a.assignmentStatus === 'Accepted') ?? null,
  );

  constructor() {
    // Required inputs aren't readable in the constructor body directly (NG8118) - effect() defers
    // the read until Angular has actually bound the input.
    effect(() => this.interventionStore.loadReferrals(this.interventionId()));

    // Only staff can create/reassign (the only actions needing this picker) - affiliates don't hold
    // AffiliateRead, so calling this for them would just surface a needless 403 toast.
    if (this.isStaff()) {
      this.loadEligibleForCurrentType();
    }
  }

  // InterventionsUpdate is staff-only - affiliates never hold it, even though some affiliates and
  // all staff hold ReferralsRespond (staff keep it for support/override cases). Used to tell the two
  // apart so an admin viewing a pool of several affiliates isn't shown an ambiguous "respond" button.
  protected isStaff(): boolean {
    return this.permissionService.hasPermission(Permission.InterventionsUpdate);
  }

  protected canCreateInCurrentStage(): boolean {
    const status = this.interventionStatus();
    return status === null || status === 'Assessment' || status === 'Referral';
  }

  // New and Eligibility come before the stage a referral can be created in.
  protected isBeforeAssessment(): boolean {
    const status = this.interventionStatus();
    return status === 'New' || status === 'Eligibility';
  }

  protected currentStageLabel(): string {
    return (this.interventionStatus() ?? '').replace(/([a-z])([A-Z])/g, '$1 $2');
  }

  // BR004: referral consent is recorded (on the Consent tab) before a referral can be created.
  protected canCreateReferral(): boolean {
    return (
      this.isStaff() &&
      !this.currentReferral() &&
      this.canCreateInCurrentStage() &&
      this.interventionStore.hasConsent('Referral')
    );
  }

  protected needsReferralConsent(): boolean {
    return (
      this.isStaff() &&
      !this.currentReferral() &&
      this.canCreateInCurrentStage() &&
      !this.interventionStore.hasConsent('Referral')
    );
  }

  protected canReassign(): boolean {
    const referral = this.currentReferral();
    if (!this.isStaff() || !referral) {
      return false;
    }

    return (
      referral.referralType === 'SELECT' &&
      referral.acceptanceStatus !== 'Accepted' &&
      referral.assignments.some((a) => a.assignmentStatus === 'Declined' || a.assignmentStatus === 'Withdrawn')
    );
  }

  protected canRespondAsAffiliate(): boolean {
    return !this.isStaff() && this.permissionService.hasPermission(Permission.ReferralsRespond);
  }

  protected canAcceptOrDecline(): boolean {
    return this.canRespondAsAffiliate() && !!this.myPoolEntry()?.isActive && !this.myAcceptedAssignment();
  }

  protected canWithdraw(): boolean {
    return this.canRespondAsAffiliate() && !!this.myAcceptedAssignment();
  }

  // Drives the pool list for both staff (full pool) and affiliates (their own row only): once an
  // affiliate has actually responded, their most recent response status is more meaningful than the
  // raw pool-membership flag, which an accepting affiliate's own entry never flips off.
  protected poolStatusLabel(item: ProviderReferralAffiliateResponse): string {
    const latestAssignment = (this.currentReferral()?.assignments ?? [])
      .filter((a) => a.affiliateId === item.affiliateId)
      .sort((a, b) => new Date(b.assignedDate).getTime() - new Date(a.assignedDate).getTime())[0];

    return latestAssignment?.assignmentStatus ?? (item.isActive ? 'Pending' : 'Closed');
  }

  protected loadEligibleForCurrentType(): void {
    const interventionTypeId = this.interventionStore.selected()?.interventionTypeId;
    if (interventionTypeId) {
      this.affiliateStore.loadEligibleAffiliates(interventionTypeId);
    }
  }

  protected createReferral(): void {
    const model = this.referralModel();
    if (!model) {
      return;
    }

    if (model === 'SELECT' && !this.selectAffiliateControl.value) {
      return;
    }

    this.interventionStore.createReferral(
      this.interventionId(),
      { selectedAffiliateId: model === 'SELECT' ? this.selectAffiliateControl.value : null },
      () => this.selectAffiliateControl.setValue(''),
    );
  }

  protected reassign(): void {
    const referral = this.currentReferral();
    if (!referral || !this.reassignAffiliateControl.value) {
      return;
    }

    this.interventionStore.reassignReferral(this.interventionId(), referral.id, {
      newAffiliateId: this.reassignAffiliateControl.value,
    });
    this.reassignAffiliateControl.setValue('');
  }

  protected accept(): void {
    const referral = this.currentReferral();
    const poolEntry = this.myPoolEntry();
    if (!referral || !poolEntry) {
      return;
    }

    this.interventionStore.acceptReferral(this.interventionId(), referral.id, poolEntry.affiliateId);
  }

  protected decline(): void {
    const referral = this.currentReferral();
    const poolEntry = this.myPoolEntry();
    if (!referral || !poolEntry || !this.declineReasonControl.value.trim()) {
      return;
    }

    this.interventionStore.declineReferral(
      referral.id,
      poolEntry.affiliateId,
      { declineReason: this.declineReasonControl.value.trim() },
      () => this.goToInterventionList(),
    );
  }

  protected withdraw(): void {
    const referral = this.currentReferral();
    const assignment = this.myAcceptedAssignment();
    if (!referral || !assignment || !this.withdrawReasonControl.value.trim()) {
      return;
    }

    this.interventionStore.withdrawReferral(
      referral.id,
      assignment.affiliateId,
      { withdrawReason: this.withdrawReasonControl.value.trim() },
      () => this.goToInterventionList(),
    );
  }

  // Declining/withdrawing drops the intervention off the affiliate's own scope (see
  // InterventionRepository.ApplyTenantScope), so staying on this page would just error on the next
  // reload - send them back to the list, which already reflects the narrower scope.
  private goToInterventionList(): void {
    void this.router.navigate(['/interventions']);
  }
}
